import React from "react";
import { formatMessage } from "lib/globalize-util";
import {
    Dialog,
    DialogTitle,
    DialogType,
    IDialogContentProps,
    IModalProps,
    mergeStyleSets,
    nuOrderTheme as theme,
    ProgressIndicator,
    Text,
} from "@nuorder/components";
import { IRollupSnapshotResponse, RollupSnapshotStatus } from "containers/folders/types/server-rollups";
import { FormatMessage } from "react-globalize";
import { DATATEST, DATA_IDS } from "constants/integration";
import { shouldRecreateSnapshot } from "helpers/server-rollup";
import classNames from "classnames";

interface IOwnProps {
    onOpenQuickRollupRequest?: () => void;
    onOpenRollup: (assortmentIds: string[] | string, isQuickRollup?: boolean) => void;
    onClose: () => void;
    open: boolean;
    isLoadingQuickRollup?: boolean;
    disableViewQuikRollup?: boolean;
    collectionSnapshot?: IRollupSnapshotResponse;
    collectionIds?: string[];
    serverRollupDisabled?: boolean;
    isAuthorizationError?: boolean;
    collectionNotFoundInRollup?: boolean;
}

const classes = mergeStyleSets({
    container: {
        minHeight: 120,
    },
    message: {
        fontSize: 14,
        wordBreak: "break-word",
        display: "block",
    },
    helperText: {
        color: theme.palette.neutralSecondary,
    },
    link: {
        paddingLeft: 4,
    },
    progressBar: {
        paddingTop: 24,
    },
    percentage: {
        color: theme.palette.neutralSecondary,
        paddingLeft: 4,
    },
});

export const OpenRollupTypeDialog: React.FunctionComponent<IOwnProps> = ({
    collectionSnapshot,
    collectionIds,
    open,
    onOpenQuickRollupRequest,
    onOpenRollup,
    onClose,
    isLoadingQuickRollup,
    disableViewQuikRollup,
    serverRollupDisabled = true,
    isAuthorizationError = false,
    collectionNotFoundInRollup = false,
}) => {
    const handleFinished = React.useCallback(() => {
        if (isAuthorizationError || !collectionSnapshot || collectionNotFoundInRollup) {
            return;
        }
        onOpenRollup([collectionSnapshot.id]);
    }, [onOpenRollup, collectionIds, collectionSnapshot, isAuthorizationError, collectionNotFoundInRollup]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOpenQuickRollupRequest = React.useCallback(() => {
        if (!collectionIds?.length) {
            onOpenQuickRollupRequest?.();
            return;
        }
        onOpenRollup(collectionIds, true);
    }, [collectionIds, onOpenRollup, onOpenQuickRollupRequest]);

    const isRecreatingSnapshot = shouldRecreateSnapshot(collectionSnapshot?.status);
    const isCollectingSnapshotData =
        !isRecreatingSnapshot && !isAuthorizationError && !serverRollupDisabled && !collectionNotFoundInRollup;
    const isSnapshotError = isAuthorizationError || serverRollupDisabled || collectionNotFoundInRollup;

    const confirmDialogTitle = React.useMemo(
        () => (
            <DialogTitle
                mainTitle={formatMessage("Opening Rollup")}
                iconColor={theme.palette.errorBase}
                {...(isSnapshotError && { iconName: "alertCircle24" })}
            />
        ),
        [isSnapshotError]
    );

    const helperText = React.useMemo(() => {
        if (collectionNotFoundInRollup) {
            return (
                <FormatMessage
                    className={classes.message}
                    data-test={DATATEST.FOLDERS.OPEN_ROLLUP_DIALOG.COLLECTION_NOT_FOUND}
                    path={
                        "We are unable to open one or more assortments in this rollup. The missing assortment(s) may be in the Trash.[br][br]Please contact your Drive Admin or Organizer to make sure all assortments in the rollup are active."
                    }
                    elements={{ br: <br /> }}
                />
            );
        }

        if (isAuthorizationError) {
            return (
                <FormatMessage
                    className={classes.message}
                    data-test={DATATEST.FOLDERS.OPEN_ROLLUP_DIALOG.AUTHORIZATION_ERROR}
                    path={
                        "We are unable to open one or more assortments in this rollup. The missing assortment(s) may be deleted, or you may not have the necessary access permissions.[br][br]Please contact your Drive Admin or Organizer to make sure all assortments in the rollup are active and shared with you."
                    }
                    elements={{ br: <br /> }}
                />
            );
        }

        if (serverRollupDisabled) {
            return (
                <FormatMessage
                    className={classes.message}
                    data-test={DATATEST.FOLDERS.OPEN_ROLLUP_DIALOG.SERVER_ROLLUP_DISABLED}
                    path={
                        "There was an issue loading the rollup. Please refresh your browser and try again.[br][br]If this issue persists, contact NuORDER Support."
                    }
                    elements={{ br: <br /> }}
                />
            );
        }

        if (!serverRollupDisabled) {
            return (
                <Text
                    as="div"
                    className={classNames(classes.message, classes.helperText)}
                    data-test={DATATEST.FOLDERS.OPEN_ROLLUP_DIALOG.HELPER_TEXT}
                >
                    {formatMessage("Please wait while we load the latest data for your rollup.")}
                </Text>
            );
        }

        return null;
    }, [isAuthorizationError, serverRollupDisabled, collectionNotFoundInRollup]);

    const percentComplete =
        collectionSnapshot?.status === RollupSnapshotStatus.Finished ? 1 : (collectionSnapshot?.progress ?? 0);

    const dialogContentProps = React.useMemo<IDialogContentProps>(
        () => ({
            type: DialogType.normal,
            closeButtonAriaLabel: formatMessage("Close"),
            showCloseButton: true,
            titleProps: {
                "data-test": DATATEST.FOLDERS.DIALOG.TITLE,
            } as any,
            subTextId: `${DATA_IDS.FOLDERS.DIALOG.OPEN_ROLLUP_CONTAINER_ID}_subtext`,
            title: confirmDialogTitle as any,
        }),
        [confirmDialogTitle]
    );

    const modalProps = React.useMemo<IModalProps>(
        () => ({
            titleAriaId: "dialogLabel",
            subtitleAriaId: "subTextLabel",
            isBlocking: false,
            styles: {
                main: { width: "600px !important", maxWidth: "100vw !important" },
            },
            containerClassName: DATA_IDS.FOLDERS.DIALOG.OPEN_ROLLUP_CONTAINER_ID,
            onDismissed() {
                handleFinished();
            },
        }),
        [handleFinished]
    );

    return (
        <Dialog hidden={!open} onDismiss={onClose} dialogContentProps={dialogContentProps} modalProps={modalProps}>
            <div className={classes.container}>
                {!!helperText && helperText}
                {isCollectingSnapshotData && (
                    <div
                        className={classes.progressBar}
                        data-test={DATATEST.FOLDERS.OPEN_ROLLUP_DIALOG.PROGRESS_INDICATOR}
                    >
                        <ProgressIndicator
                            label={
                                <Text>
                                    {collectionSnapshot?.progress_label || formatMessage("Preparing your rollup")}
                                    <Text variant="smallPlus" className={classes.percentage}>
                                        ({percentComplete < 1 ? (percentComplete * 100).toFixed(0) : "100"}%)
                                    </Text>
                                </Text>
                            }
                            description={collectionSnapshot?.progress_description}
                            percentComplete={percentComplete}
                        />
                    </div>
                )}
            </div>
        </Dialog>
    );
};
